/**
 * Integration (connector) admin API. Added for the FE↔BE integration (Round 5+).
 *
 * Softknack-internal connector authoring:
 *   PATCH /integrations/:id                       — edit display fields
 *   POST  /integrations/:id/mcp                   — wire/replace MCP server (Round 6)
 *   POST  /integrations/:id/mcp/resync            — resync MCP tool list (Round 6)
 *   DELETE /integrations/:id/mcp                  — disconnect MCP (Round 6)
 *   POST  /integrations/:id/webhook/rotate-secret — rotate webhook secret (Round 7)
 *
 * All are internal=true.
 */
import { apiFetch } from './client';

export interface EditDetailsInput {
  display_name?: string;
  category?: string;
  change_delivery?: 'none' | 'webhook' | 'poll';
  purpose?: string;
}

// Subset of the integration DTO the Manage-MCP drawer needs. The single-GET
// includes `mcp` (real server_url + transport from bridge_mcp_config) for wired
// mcp-class connectors; null/absent otherwise.
export interface IntegrationDetail {
  integration_id: string;
  adapter_class: 'mcp' | 'openapi' | 'custom';
  mcp?: { server_url: string; transport: 'sse' | 'streamable_http' } | null;
  // True for built-in connectors (HubSpot/Zoho) whose MCP server URL is managed
  // by Softknack — the drawer renders Server URL + Transport read-only. The BE
  // also rejects edits to these, so this only governs presentation.
  managed?: boolean;
}

/** GET one integration, including its real MCP config (server_url + transport). */
export async function getIntegrationDetail(integrationId: string): Promise<IntegrationDetail> {
  return apiFetch<IntegrationDetail>(`/integrations/${integrationId}`, { internal: true });
}

/** PATCH connector display details. Returns the updated integration DTO. */
export async function editIntegrationDetails(integrationId: string, input: EditDetailsInput): Promise<unknown> {
  return apiFetch(`/integrations/${integrationId}`, {
    method: 'PATCH',
    body: input,
    internal: true,
  });
}

// --- MCP management (Round 6) ---

// FE transport tokens vs BE enum: the FE uses 'sse' | 'stdio', but its UI labels
// 'stdio' as "streamable-http". The BE CHECK allows 'sse' | 'streamable_http'.
// So map FE 'stdio' → BE 'streamable_http' (and 'sse' → 'sse'). BE is canonical.
export type FeTransport = 'sse' | 'stdio';
function toBeTransport(t: FeTransport): 'sse' | 'streamable_http' {
  return t === 'stdio' ? 'streamable_http' : 'sse';
}

/** Wire (or replace) the MCP server for an integration. */
export async function manageMcp(integrationId: string, serverUrl: string, transport: FeTransport): Promise<unknown> {
  return apiFetch(`/integrations/${integrationId}/mcp`, {
    method: 'POST',
    body: { server_url: serverUrl, transport: toBeTransport(transport) },
    internal: true,
  });
}

/** Re-sync the MCP tool list. The BE re-reads the advertised tools; we pass the
 *  currently-known tool names so it can flag genuinely new ones. Returns the
 *  full + newly-discovered tool name lists. */
export async function resyncMcp(
  integrationId: string,
  knownTools: string[],
): Promise<{ tools: string[]; new_tools: string[] }> {
  return apiFetch<{ tools: string[]; new_tools: string[] }>(`/integrations/${integrationId}/mcp/resync`, {
    method: 'POST',
    body: { tools: knownTools.map((name) => ({ name })) },
    internal: true,
  });
}

/** Disconnect the MCP server from an integration. */
export async function disconnectMcp(integrationId: string): Promise<void> {
  await apiFetch<void>(`/integrations/${integrationId}/mcp`, { method: 'DELETE', internal: true });
}

// --- Webhook signing secret (Round 7) ---

/**
 * Rotate the inbound webhook signing secret. The BE mints a new secret, stores
 * it by reference in the vault, and returns the raw value ONCE (it cannot be
 * retrieved again). The FE shows it so the admin can copy it into the vendor.
 */
export async function rotateWebhookSecret(
  integrationId: string,
): Promise<{ secret: string; rotated_at: string }> {
  return apiFetch<{ secret: string; rotated_at: string }>(
    `/integrations/${integrationId}/webhook/rotate-secret`,
    { method: 'POST', internal: true },
  );
}
